import Cookies from "js-cookie";

export type MapProvider = "yandex" | "google";

const RUSSIAN_LANGUAGE_NAMES = [
  "russian",
  "русский",
  "русский язык",
  "ru",
];

export function isRussianLanguageName(languageName?: string | null) {
  const name = (languageName || "").trim().toLowerCase();
  if (!name) return false;
  return (
    RUSSIAN_LANGUAGE_NAMES.some((item) => name.includes(item)) ||
    name === "ru" ||
    name.startsWith("ru-") ||
    name.startsWith("ru_")
  );
}

export function resolveMapProvider(languageName?: string | null): MapProvider {
  return isRussianLanguageName(languageName) ? "yandex" : "google";
}

export function getGoogleMapsApiKey() {
  const key = String(import.meta.env.VITE_Google_MAP_KEY || "").trim();
  if (!key || /google map api key/i.test(key)) {
    // Fallback used elsewhere in the project when .env still has a placeholder.
    return "AIzaSyAMZ4GbRFYSevy7tMaiH5s0JmMBBXc0qBA";
  }
  return key;
}

export function parseLatLng(content?: string | null) {
  if (!content) return null;
  const raw = String(content).trim();
  if (!raw) return null;

  // Mobile / some clients: {"latitude":55.66,"longitude":37.77}
  if (raw.startsWith("{")) {
    try {
      const obj = JSON.parse(raw) as {
        latitude?: unknown;
        longitude?: unknown;
        lat?: unknown;
        lng?: unknown;
        lon?: unknown;
      };
      const lat = Number(obj.latitude ?? obj.lat);
      const lng = Number(obj.longitude ?? obj.lng ?? obj.lon);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        return { lat, lng };
      }
    } catch {
      /* fall through */
    }
  }

  // Web / legacy: "55.66,37.77"
  const match = raw.match(
    /(-?\d+(?:\.\d+)?)\s*[,;\s]\s*(-?\d+(?:\.\d+)?)/,
  );
  if (match) {
    const lat = Number(match[1]);
    const lng = Number(match[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { lat, lng };
    }
  }

  return null;
}

export function getExternalMapUrl(
  provider: MapProvider,
  lat: number,
  lng: number,
) {
  if (provider === "yandex") {
    return `https://yandex.ru/maps/?pt=${lng},${lat}&z=16&l=map`;
  }
  return `https://www.google.com/maps?q=${lat},${lng}&z=16`;
}

export function getMapEmbedUrl(
  provider: MapProvider,
  lat: number,
  lng: number,
  zoom = 15,
) {
  if (provider === "yandex") {
    return `https://yandex.ru/map-widget/v1/?ll=${lng}%2C${lat}&z=${zoom}&pt=${lng}%2C${lat},pm2rdm&l=map`;
  }

  // output=embed works without Maps Embed API billing quirks.
  return `https://maps.google.com/maps?q=${lat},${lng}&z=${zoom}&hl=en&output=embed`;
}

/** Static PNG preview — reliable in chat (iframe widget often blank / cookie wall). */
export function getStaticMapUrl(
  lat: number,
  lng: number,
  zoom = 15,
  width = 450,
  height = 250,
) {
  const w = Math.min(650, Math.max(1, Math.round(width)));
  const h = Math.min(450, Math.max(1, Math.round(height)));
  return `https://static-maps.yandex.ru/1.x/?ll=${lng},${lat}&size=${w},${h}&z=${zoom}&l=map&pt=${lng},${lat},pm2rdm&lang=ru_RU`;
}

export function getNominatimAcceptLanguage(provider: MapProvider) {
  return provider === "yandex" ? "ru" : "en";
}

export function getLanguageIdFromCookie() {
  return Cookies.get("language_id") || "";
}
